import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  ButtonProps,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Icon,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { RiFilter2Line } from "@remixicon/react";
import { useEffect, useState } from "react";
import { SelectOption } from "../../constants/SelectOption";
import useCustomToast from "../../lib/useCustomToast";
import { useFetchCategory } from "../../store/useFetchCategory";
import { CardSkeleton } from "../skeleton/CardSkeleton";
import { axiosPublic } from "../../utils/AxiosInstance";

interface Props extends ButtonProps {
  onSelected: (select: SelectOption | undefined) => void;
  onResetFilter: (reset: boolean) => void;
}

const FilterDrawer = ({ onSelected, onResetFilter, ...rest }: Props) => {
  const { data, error, isLoading, fetchCategory } = useFetchCategory();
  const [selected, setSelected] = useState<SelectOption | undefined>(undefined);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useCustomToast();

  useEffect(() => {
    if (!data) {
      fetchCategory();
    }
  }, [fetchCategory, data]);

  useEffect(() => {
    if (error) {
      return toast({
        description: error,
      });
    }
  }, [error]);

  const handleSelect = (value: any) => {
    setSelected(value);
    value && onSelected(value);
    onClose();
  };

  const handleToggle = (category: string) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  const handleReset = () => {
    setSelected(undefined);
    setExpandedCategory(null);
    onResetFilter(true);
    toast({
      title: "Berhasil mereset filter",
      status: "success",
    });
    onClose();
  };

  return (
    <>
      <Button
        variant={"ghost"}
        colorScheme="black"
        leftIcon={<Icon as={RiFilter2Line} />}
        onClick={onOpen}
        {...rest}
      >
        Filter
      </Button>

      <Drawer isOpen={isOpen} onClose={onClose} placement="bottom" size={"sm"}>
        <DrawerOverlay />
        <DrawerContent minH={"50dvh"}>
          <DrawerCloseButton />
          <DrawerHeader>Filter Kategori</DrawerHeader>

          <DrawerBody>
            <VStack w={"100%"} align={"stretch"} gap={0}>
              {isLoading ? (
                <CardSkeleton row={1} column={10} height={30} />
              ) : (
                data?.map((category) => (
                  <Box key={category.name} width="full">
                    <HStack
                      p={2}
                      fontSize={[10, null, 12]}
                      fontWeight={
                        expandedCategory === category.name
                          ? "semibold"
                          : "normal"
                      }
                      justify={"space-between"}
                      cursor={"pointer"}
                      onClick={() => handleToggle(category.name)}
                    >
                      <Text>{category.name}</Text>

                      {expandedCategory === category.name ? (
                        <ChevronUpIcon />
                      ) : (
                        <ChevronDownIcon />
                      )}
                    </HStack>

                    <Collapse in={expandedCategory === category.name}>
                      <VStack
                        w={"100%"}
                        align={"stretch"}
                        my={1}
                        pl={2}
                        gap={0}
                      >
                        {category.subCategories.map((item) => (
                          <Box
                            key={item.id}
                            as="button"
                            py={2}
                            px={4}
                            textAlign={"start"}
                            onClick={() => handleSelect(item)}
                          >
                            <Text
                              fontSize={[10, null, 12]}
                              color={
                                selected && selected.name === item.name
                                  ? "red.400"
                                  : undefined
                              }
                              fontWeight={
                                selected && selected.name === item.name
                                  ? "semibold"
                                  : "normal"
                              }
                            >
                              {item.name}
                            </Text>
                          </Box>
                        ))}
                      </VStack>
                    </Collapse>
                  </Box>
                ))
              )}
            </VStack>
          </DrawerBody>

          <DrawerFooter>
            <Button
              colorScheme="red"
              size={["xs", null, "sm"]}
              variant={"outline"}
              onClick={handleReset}
            >
              Reset Filter
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default FilterDrawer;
